import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { User, Lock, Bell, Palette, ChevronRight, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';
import ThemeContext from '../../contexts/ThemeContext';
import { THEMES } from '../../utils/theme';

const SettingsPage = () => {
  const { user, updateUser } = useContext(AuthContext);
  const { theme, setTheme } = useContext(ThemeContext);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Notification settings state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [materialUpdates, setMaterialUpdates] = useState(true);
  const [ratingNotifications, setRatingNotifications] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);
  
  // Theme settings
  const [selectedTheme, setSelectedTheme] = useState(theme);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (!currentPassword) {
      toast.error('Current password is required');
      return;
    }
    
    if (!newPassword) {
      toast.error('New password is required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }
    
    try {
      setChangingPassword(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to change password');
      }
      
      // Reset form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      toast.success('Password changed successfully');
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(error.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleNotificationSave = async () => {
    try {
      setSavingNotifications(true);
      
      // In a real app, this would save to the backend
      await new Promise(resolve => setTimeout(resolve, 500)); // Fake API call
      
      toast.success('Notification preferences saved');
    } catch (error) {
      console.error('Failed to save notification settings:', error);
      toast.error('Failed to save notification settings');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleThemeChange = async (newTheme) => {
    try {
      setSelectedTheme(newTheme);
      
      // Update theme in context
      setTheme(newTheme);
      
      // Save preference to backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/me/theme`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          themePreference: newTheme
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save theme preference');
      }
      
      const data = await response.json();
      
      // Update user in context with new theme preference
      updateUser({
        themePreference: data.themePreference
      });
      
      toast.success('Theme updated');
    } catch (error) {
      console.error('Theme update error:', error);
      toast.error('Failed to update theme');
    }
  };

  const ThemeOption = ({ value, label, color, children }) => {
    return (
      <div 
        className={`relative flex items-center p-4 border ${
          selectedTheme === value 
            ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
            : 'border-gray-200 dark:border-gray-700'
        } rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750`}
        onClick={() => handleThemeChange(value)}
      >
        <div className="h-10 w-10 rounded-full mr-3" style={{ backgroundColor: color }}></div>
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{label}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{children}</div>
        </div>
        {selectedTheme === value && (
          <CheckCircle className="h-5 w-5 text-red-500 absolute top-4 right-4" />
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Settings</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Navigation sidebar */}
        <div className="md:col-span-1">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Settings
              </h3>
            </div>
            <nav className="px-4 py-2">
              <ul>
                <li>
                  <a 
                    href="#account" 
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-900 dark:text-white rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                  >
                    <User className="mr-3 h-5 w-5" />
                    <span>Account</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="#password" 
                    className="flex items-center px-3 py-2 mt-1 text-sm font-medium text-gray-600 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-750 hover:text-gray-900 dark:hover:text-white"
                  >
                    <Lock className="mr-3 h-5 w-5" />
                    <span>Password</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="#notifications" 
                    className="flex items-center px-3 py-2 mt-1 text-sm font-medium text-gray-600 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-750 hover:text-gray-900 dark:hover:text-white"
                  >
                    <Bell className="mr-3 h-5 w-5" />
                    <span>Notifications</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="#theme" 
                    className="flex items-center px-3 py-2 mt-1 text-sm font-medium text-gray-600 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-750 hover:text-gray-900 dark:hover:text-white"
                  >
                    <Palette className="mr-3 h-5 w-5" />
                    <span>Theme</span>
                  </a>
                </li>
              </ul>
            </nav>
          </div>
          
          <div className="mt-6">
            <Link
              to="/profile"
              className="inline-flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              <ChevronRight className="rotate-180 mr-1 h-5 w-5" />
              Back to Profile
            </Link>
          </div>
        </div>
        
        {/* Settings content */}
        <div className="md:col-span-2 space-y-6">
          {/* Account Settings */}
          <div id="account" className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Account Settings
              </h3>
            </div>
            <div className="px-4 py-5 sm:px-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{user?.name}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{user?.email}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">School</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{user?.school?.name}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Role</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{user?.role}</dd>
                </div>
              </dl>
              
              <div className="mt-6">
                <Link
                  to="/profile"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Edit Profile
                </Link>
              </div>
            </div>
          </div>
          
          {/* Password Settings */}
          <div id="password" className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Change Password
              </h3>
            </div>
            <div className="px-4 py-5 sm:px-6">
              <form onSubmit={handlePasswordChange}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="current-password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  
                  {newPassword && newPassword.length < 8 && (
                    <div className="flex items-center text-sm text-yellow-600 dark:text-yellow-400">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      Password must be at least 8 characters long
                    </div>
                  )}
                  
                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <div className="flex items-center text-sm text-red-600 dark:text-red-400">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      Passwords do not match
                    </div>
                  )}
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={changingPassword}
                      className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                        changingPassword ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                    >
                      {changingPassword ? 'Changing Password...' : 'Change Password'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          
          {/* Notification Settings */}
          <div id="notifications" className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Notification Settings
              </h3>
            </div>
            <div className="px-4 py-5 sm:px-6">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="email-notifications"
                      name="email-notifications"
                      type="checkbox"
                      checked={emailNotifications}
                      onChange={(e) => setEmailNotifications(e.target.checked)}
                      className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300 dark:border-gray-600 rounded"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="email-notifications" className="font-medium text-gray-700 dark:text-gray-300">
                      Email Notifications
                    </label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Receive email notifications for important updates
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="material-updates"
                      name="material-updates"
                      type="checkbox"
                      checked={materialUpdates}
                      onChange={(e) => setMaterialUpdates(e.target.checked)}
                      className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300 dark:border-gray-600 rounded"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="material-updates" className="font-medium text-gray-700 dark:text-gray-300">
                      Material Updates
                    </label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Get notified when new materials are added to courses you're subscribed to
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="rating-notifications"
                      name="rating-notifications"
                      type="checkbox"
                      checked={ratingNotifications}
                      onChange={(e) => setRatingNotifications(e.target.checked)}
                      className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300 dark:border-gray-600 rounded"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="rating-notifications" className="font-medium text-gray-700 dark:text-gray-300">
                      Rating Feedback
                    </label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Receive notifications when someone interacts with your materials
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleNotificationSave}
                    disabled={savingNotifications}
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                      savingNotifications ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {savingNotifications ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Theme Settings */}
          <div id="theme" className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Theme Settings
              </h3>
            </div>
            <div className="px-4 py-5 sm:px-6">
              <div className="space-y-4">
                <ThemeOption value={THEMES.DARK} label="Dark" color="#121212">
                  Dark theme, easier on the eyes at night
                </ThemeOption>
                
                <ThemeOption value={THEMES.LIGHT} label="Light" color="#ffffff">
                  Light theme with clean white background
                </ThemeOption>
                
                <ThemeOption value={THEMES.BLUE} label="Blue" color="#e3f2fd">
                  Calm blue theme for a soothing experience
                </ThemeOption>
                
                <ThemeOption value={THEMES.GREEN} label="Green" color="#e8f5e9">
                  Fresh green theme for a natural look
                </ThemeOption>
                
                <ThemeOption value={THEMES.PURPLE} label="Purple" color="#f3e5f5">
                  Elegant purple theme for a touch of style
                </ThemeOption>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;